import { createFileRoute, Outlet } from "@tanstack/react-router";
import { RecordSessionProvider } from "@/components/entity-tabs/record-session";
import { NotificationPeekProvider } from "@/components/peek/notification-peek-context";
import { PeekPaneLayout } from "@/components/peek/notification-peek-pane";
import { useWorkspaceRoot } from "@/lib/api";
import { useAuthState } from "@/lib/auth-state";
import {
	shouldRedirectAnonymousToSignIn,
	useAnonymousSignInRedirect,
} from "@/lib/sign-in-redirect";

export const Route = createFileRoute("/$owner")({
	loader: ({ context: { queryClient }, params }) => {
		if (params.owner === "me") return;
		// Fire-and-forget — awaiting here blocks child routes behind the router's
		// generic pending UI instead of each page's shape-matched skeletons.
		void queryClient.prefetchQuery(
			useWorkspaceRoot.queryOptions(params.owner, { slug: params.owner }),
		);
	},
	// Avoid generic pending UI while workspace pages settle; account activity
	// skips workspace resolution and loads in its child route.
	pendingComponent: () => null,
	component: OwnerLayoutRoute,
});

function OwnerLayoutRoute() {
	const { owner } = Route.useParams();

	// Auth gate for the whole owner subtree. Workspace owners reuse the query
	// prefetched by the loader; the reserved account owner skips resolution and
	// gates directly on the authenticated session.
	const {
		data: workspaceRoot,
		isLoading: isWorkspaceLoading,
		error: workspaceError,
	} = useWorkspaceRoot(owner === "me" ? null : owner, { slug: owner });
	const { session, isReady: authReady } = useAuthState();
	// Only a refusal — chiefly the 401 a private org answers anonymously —
	// should reach sign-in. Outages and rate limits render their own state.
	const isRedirectingToSignIn = useAnonymousSignInRedirect({
		active: shouldRedirectAnonymousToSignIn({
			settled: !isWorkspaceLoading,
			authReady,
			session,
			resolved: !!workspaceRoot?.workspace,
			error: workspaceError,
		}),
		owner,
	});

	// Render nothing while the sign-in navigation lands, so no page in this
	// subtree flashes an auth error on the way to the login screen.
	if (isRedirectingToSignIn) return null;

	return (
		<NotificationPeekProvider>
			{/* Above both the page and the pane: a record field the pane edits
			    reaches the record page's draft through this store. */}
			<RecordSessionProvider>
				<PeekPaneLayout>
					<Outlet />
				</PeekPaneLayout>
			</RecordSessionProvider>
		</NotificationPeekProvider>
	);
}
